// static/js/components/ui/IntentIcons.jsx

/**
 * This JSX module provides a visual interface component named `IntentIcons`, designed to display
 * interactive icons representing different user intents within a React-based web application.
 * Each icon serves as a button that, when clicked, triggers a callback function passed through
 * the `onSelect` prop, signaling the selected user intent to the application.
 *
 * The intents represented include actions such as fetching metadata, visualizing data, filtering
 * data, searching within documents, generating reports, and accessing help or additional information.
 * Each intent is visually distinguished by its own unique icon and label, and offers color-coded
 * feedback for enhanced user interaction.
 *
 * The component also provides a list of suggested actions for each intent, aiding users in understanding
 * what actions are possible within the application context. This list includes tasks like creating
 * word clouds, generating term trends, filtering journal entries, and more, making the component
 * a central piece of the application's interactive user interface.
 */

import React from 'react';
import { Search, HelpCircle, Filter, BarChart2, Database, FileText } from 'lucide-react';

const IntentIcons = ({ onSelect }) => {

  // Intent types with their icons and suggestions
  const intents = [
    {
      id: 'metadata',
      icon: <FileText className="h-5 w-5" />,
      label: 'Info',
      color: 'text-orange-500',
      suggestions: [
        'What is the total number of documents created in my last upload?',
        'How many journal entries do I have?',
        'What date range do my documents span?',
        'Show my journal categories.',
        'Show my total and average word count and average duration.',
        'What is the total number of journals and average word count for my journals over the date range...'
      ]
    },
    {
      id: 'visualize',
      icon: <BarChart2 className="h-5 w-5" />,
      label: 'Visualize',
      color: 'text-purple-500', 
      suggestions: [
        'Create a word cloud using outdoor activity terms',
        'Generate a term trend using the top 7 country names and a yearly time granularity',
        'Using a monthly time granularity show the top 5 trending emotion words for the date range...',
        'Display a sentiment timeline using a yearly time granularity'
      ]
    },
    {
      id: 'filter',
      icon: <Filter className="h-5 w-5" />,
      label: 'Filter',
      color: 'text-yellow-500',
      suggestions: [
        'Fetch the 5 most recent dream journals for the year...',
        'Retrieve the first 5 documents from Christmas week of...',
        'Show the first 7 entries in the fitness journal category between... '
      ]
    },
    {
      id: 'search',
      icon: <Search className="h-5 w-5" />,
      label: 'Search',
      color: 'text-green-500',
      suggestions: [
        'Using search type = keyword, find documents matching: "Ukumehame" and "Launiupoko"',
        'Search documents for...',
        'Find paragraphs about...practicing latte art, since last november',
        'Find paragraphs from my diaries since last summer using semantic search matching: Those times I felt especially grateful for my good fortune. Return the top 7 results.',
      ]
    },
    {
      id: 'generate',
      icon: <Database className="h-5 w-5" />,
      label: 'Generate',
      color: 'text-red-500',
      suggestions: [
        'Generate word counts for last month using the brand words term set. Just the dataset.',
        'Create a term trend dataset for summer of last year. No image needed.',
        'Generate first POV summaries for the attached docset.',
        'Fetch the summarized versions of the attached docset for the bullet-list transform.',
        'Generate the N:1 summary of summaries for the attached docset using the bullet-list transform.',
        'Fetch the N:1 summary of summaries for the attached docset using the bullet-list transform.',
      ]
    },
    {
      id: 'ask',
      icon: <HelpCircle className="h-5 w-5" />,
      label: 'Ask',
      color: 'text-blue-500',
      suggestions: [
        'What are the web app features?',
        'Tell me about security features',
        'Summarize the Privacy Policy',
        'Show FAQs',
        'Do I have any pending background tasks?'
      ]
    },
  ];

  // Handle intent selection
  const handleIntentClick = (intent) => {
    if (onSelect) {
      onSelect(intent);
    }
  };

  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {intents.map((intent) => (
        <button
          key={intent.id}
          onClick={() => handleIntentClick(intent)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full 
            bg-primary-700/10 hover:bg-primary-700/20 transition-colors 
            ${intent.color}`}
        >
          {intent.icon}
          <span className="text-sm text-primary-text">{intent.label}</span>
        </button>
      ))}
    </div>
  );
};

export default IntentIcons;